import type { SmgSplitSession, TreatmentConfig } from '@smg-real-estate/vue-experiments';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useSplitStore = defineStore('split', () => {
  const treatments = ref<Record<string, TreatmentConfig>>({});
  const splitSessionKey = ref('');

  const setTreatment = (
    featureFlagName: string,
    treatmentWithConfig: { treatment: string; config?: unknown },
  ): void => {
    let config: unknown = treatmentWithConfig.config;

    if (typeof treatmentWithConfig.config === 'string') {
      try {
        config = JSON.parse(treatmentWithConfig.config);
      } catch {
        console.warn(`Failed to parse config for feature flag "${featureFlagName}".`);
        config = undefined;
      }
    }

    treatments.value = {
      ...treatments.value,
      [featureFlagName]: {
        treatment: treatmentWithConfig.treatment,
        config,
      },
    };
  };

  const setSplitSessionKey = (sessionKey: string): void => {
    splitSessionKey.value = sessionKey;
  };

  const initFromSession = (smgSplitSession: SmgSplitSession): void => {
    treatments.value = {};

    Object.entries(smgSplitSession.splitTreatmentsWithConfig).forEach(
      ([featureFlagName, treatmentWithConfig]) => {
        setTreatment(featureFlagName, treatmentWithConfig);
      },
    );
    setSplitSessionKey(smgSplitSession.splitSessionKey);
  };

  const getTreatment = (featureFlagName: string): TreatmentConfig | undefined => {
    return treatments.value[featureFlagName];
  };

  const isTreatmentOn = (featureFlagName: string): boolean => {
    return getTreatment(featureFlagName)?.treatment === 'on';
  };

  const updateSplitStoreFromApi = (smgSplitSession: SmgSplitSession | undefined): void => {
    if (!smgSplitSession) {
      console.info('SplitIO session not initialized');
      return;
    }

    initFromSession(smgSplitSession);
  };

  return {
    treatments,
    splitSessionKey,
    setTreatment,
    setSplitSessionKey,
    initFromSession,
    getTreatment,
    isTreatmentOn,
    updateSplitStoreFromApi,
  };
});
